'use client';

import { FC, useEffect, useState } from 'react';

import classNames from 'classnames';

import { Tab, TabProps } from '../Tab/Tab';

interface Props {
   tabs: Array<TabProps>;
   activeTab?: number;
   showContainer?: boolean;
   stretch?: boolean;
   height?: string;
   visualizeChanges?: boolean;
   alignment?: 'left' | 'center' | 'right';
   uniform?: boolean;
   tabStyle?: 'simple' | 'classic';
   onTabChange?: (index: number) => void;
   onTabClick?: (index: number) => void;
}

export const Tabs: FC<Props> = ({ tabs, activeTab = 0, showContainer = true, stretch = false, height, visualizeChanges = true, alignment = 'left', onTabChange, onTabClick, uniform = false, tabStyle = 'simple' }) => {
   const [currentTab, setCurrentTab] = useState(activeTab);

   const handleTabClick = (index: number) => {
      if (index !== currentTab && visualizeChanges) {
         setCurrentTab(index);
         onTabChange?.(index);
      }

      onTabClick?.(index);
   };

   useEffect(() => setCurrentTab(activeTab), [activeTab]);

   return (
      <div
         data-testid="tabs-container"
         className={classNames(
            'w-full flex flex-col',
            { 'items-start': alignment === 'left' },
            { 'items-center': alignment === 'center' },
            { 'items-end': alignment === 'right' },
            { 'box-shadow-component-navigation-tabs-container': showContainer },
            { 'box-shadow-component-navigation-tabs-container-color-simple': showContainer && tabStyle === 'simple' },
            { 'box-shadow-component-navigation-tabs-container-color-classic': showContainer && tabStyle === 'classic' }
         )}
         style={{
            boxShadow: tabStyle === 'simple' ? 'inset 0 calc(-1 * var(--component-navigation-tab-container-border-width-inactive)) 0 var(--component-navigation-tab-container-color-border-inactive)' : undefined
         }}
      >
         <div data-testid={`tabs${stretch ? '--stretched' : ''}`} className={classNames('grid', { 'w-full': stretch }, { 'gap-component-navigation-tabs-spacing-gap': tabStyle === 'classic' })} style={{ height, gridTemplateColumns: `repeat(${tabs.length}, minmax(0, ${uniform ? '1fr' : 'auto'}))` }}>
            {tabs.map((tab, index) => (
               <Tab
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${tab.href}-${index}`}
                  tabStyle={tabStyle}
                  width={stretch ? '100%' : undefined}
                  id={tab.id}
                  label={tab.label}
                  leadingIcon={tab.leadingIcon}
                  trailingIcon={tab.trailingIcon}
                  href={tab.href}
                  target={tab.target}
                  active={currentTab === index}
                  onClick={() => handleTabClick(index)}
               />
            ))}
         </div>
      </div>
   );
};
